import React, {
	useEffect, useState, useRef, useMemo, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Shot, ShotFeed } from '../../stores/reducers/shots';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import Dialog from '../general-components/dialog';
import Clock from '../general-components/clock';
import * as SessionActions from '../../stores/actions/session';
import * as NotesActions from '../../stores/actions/notes';
import * as NotificationActions from '../../stores/actions/notifications';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import {
	TABS,
	META_MENU_ROUTES,
	META_MENU_STATES,
	HELP_SUBMENU,
	MetaMenuOptions,
} from '../../stores/reducers/session';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { BROADCAST_STATES } from '../../stores/reducers/broadcasting';
import { NOTIFICATION_STYLES } from '../../stores/reducers/notifications';
import Avatar from '../general-components/avatar';
import NotificationsView from '../main-video/notifications-view';
import useShots from '../../contexts/shots/provider';
import { getChannelLogoFromMeeting, logOut } from '../../utils/utils';
import { GreenroomState as GS } from '../../stores/types/types';
import { HomeNotification, NOTIFICATION_TYPES } from '../../stores/reducers/home';
import * as Alerts from '../general-components/alert-service';
import { LoadNotifications } from '../../connection/home';
import TellyChannelLogo from '../../images/app-store-icon.png';
import Help from '../help/help';
import BroadcastControls from '../broadcasting/broadcast-controls';
import StreamBadge from '../broadcasting/stream-badge';
import HeaderLeftControls from '../header-components/header-left-controls';
import ScheduledStreamCommsModal from '../broadcasting/scheduled-stream-comms-modal';
import { isThirtySecondsToStart } from '../../utils/scheduled-streaming';

export default function Header(props: any) {
	const dispatch = useDispatch();
	const { socket } = props;
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const avatarPopoverOpen = useSelector((s: GS) => s.SessionReducer.avatarPopoverOpen);
	const activeTab = useSelector((s: GS) => s.SessionReducer.activeTab);
	const watcherOnly = useSelector((s: GS) => s.SessionReducer.watcher_only);
	const metaMenuState = useSelector((s: GS) => s.SessionReducer.metaMenuState);
	const metaMenuActiveRoute = useSelector((s: GS) => s.SessionReducer.metaMenuActiveRoute);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const notesDrawerOpen = useSelector((s: GS) => s.NotesReducer.notesDrawerOpen);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector((s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const goLiveButtonClicked = useSelector((s: GS) => s.BroadcastReducer.goLiveButtonClicked);
	const showDebugUI = useSelector((s: GS) => s.OpenTokReducer.showDebugUI);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const helpMenuOpen = useSelector((s: GS) => s.SessionReducer.helpMenuOpen);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);
	const backstageCommsActive = useSelector((s: GS) => s.SettingsReducer.backstageCommsActive);

	const [currentTime, setCurrentTime] = useState(new Date());
	const [dialogRight, setDialogRight] = useState<boolean>(false);
	const [scheduledStreamCommsModalOpen, setScheduledStreamCommsModalOpen] = useState<boolean>(false);
	const ignoredCommsModal = useRef<boolean>(false);

	let clockInterval: NodeJS.Timeout;
	const dialog = useRef<any>(null);

	const { currentShot } = useShots();
	const {
		shot_id: shotId,
		shot_data: {
			shotFeeds,
			shotDuration,
			autoDuration,
		},
	} = currentShot;

	const setIgnoredCommsModal = (ignored: boolean) => {
		ignoredCommsModal.current = ignored;
	}

	const updateClock = () => {
		clockInterval = setInterval(() => {
			setCurrentTime(new Date());

			if (!meeting?.meeting) {
					console.log('no meeting =>', meeting);
					return;
			}

			// if we are a producer, we have a scheduled stream, comms are off, and we are not in the modal
			if (
				scheduledStream
				&& host
				&& !backstageCommsActive
			) {
				const isLive = roomState === ROOM_STATES.SHOWTIME
					&& serverState === BROADCAST_STATES.BROADCAST_STARTED;

				const timeJoinRoom = JSON.parse(localStorage.getItem('time-join-rooms') || '{}');
				const now = Date.now();

				if (
					!timeJoinRoom?.[meeting?.meeting] // if we have never been in this room before
					// if last in room was more than 24 hours ago, reset
					|| (timeJoinRoom?.[meeting?.meeting]?.joinTime + (24 * 60 * 60 * 1000) < now)
				) {
					timeJoinRoom[meeting.meeting] = {
						joinTime: now,
					};
					localStorage.setItem('time-join-rooms', JSON.stringify(timeJoinRoom));
				} else if (!isLive && timeJoinRoom?.[meeting?.meeting]) { // clear it if we are not live
					delete timeJoinRoom[meeting.meeting];
					localStorage.setItem('time-join-rooms', JSON.stringify(timeJoinRoom));
				}

				if (
					(isLive && timeJoinRoom?.[meeting?.meeting]?.joinTime + (5 * 60 * 1000) > now)
					|| (!isLive && isThirtySecondsToStart(scheduledStream.startTime))
				) {
					if (!scheduledStreamCommsModalOpen && !ignoredCommsModal.current) {
						setScheduledStreamCommsModalOpen(true);
					}
				}
			}
		}, 5 * 1000);
	};

	useEffect(() => {
		updateClock();
		return () => {
			clearInterval(clockInterval);
		};
	}, [
		scheduledStream,
		host,
		backstageCommsActive,
		roomState,
	]);

	const getUrlForBackButton = (): string => {
		const { origin, pathname } = window.location;
		const producerString = '/producer';
		const isProducerUrl = pathname.startsWith(producerString);

		return `${origin}${isProducerUrl ? producerString : ''}`;
	};

	// rather than go back in redux and have to manage disconnecting all the
	// websockets and webrtc connection, just reload
	// this is not as smooth but is basically foolproof
	// eslint-disable-next-line @typescript-eslint/no-shadow
	const backToBroadcastsListClicked = (dialogRight: boolean) => {
		setDialogRight(dialogRight);
		dialog.current.confirm(
			'Are you sure you want to leave this greenroom?',
			'You can select a different event from the list of Broadcasts.',
			'Switch Greenroom',
			'Cancel',
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(confirmed: boolean, cancelled: boolean) => {
				if (confirmed) {
					window.location.href = getUrlForBackButton();
				}
			},
		);
	};

	function confirmSignout() {
		setDialogRight(true);
		dialog.current?.confirm(
			'Are you sure you want to sign out?',
			'You will be taken back to the sign in form.',
			'Sign Out',
			'Cancel',
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(confirmed: boolean, cancelled: boolean) => {
				if (confirmed) {
					logOut(host);
				}
			},
		);
	}

	const toggleDrawer = (metaMenuRoute: string) => {
		if (metaMenuState === META_MENU_STATES.CLOSED) {
			dispatch(SessionActions.setMetaMenuRoute(metaMenuRoute));
			dispatch(SessionActions.setMetaMenuState(META_MENU_STATES.OPENING));
		} else if (metaMenuState === META_MENU_STATES.OPEN) {
			if (metaMenuRoute !== metaMenuActiveRoute) {
				dispatch(SessionActions.setMetaMenuRoute(metaMenuRoute));
			} else {
				dispatch(SessionActions.setMetaMenuState(META_MENU_STATES.CLOSING));
			}
		}
	};

	const sharingDrawerOpen = useMemo(() => {
		if (
			(
				metaMenuState === META_MENU_STATES.OPENING
				|| metaMenuState === META_MENU_STATES.OPEN
			)
			&& metaMenuActiveRoute === META_MENU_ROUTES.SHARING
		) {
			return true;
		}

		return false;
	}, [metaMenuState, metaMenuActiveRoute]);

	const settingsDrawerOpen = useMemo(() => {
		if (
			(
				metaMenuState === META_MENU_STATES.OPENING
				|| metaMenuState === META_MENU_STATES.OPEN
			)
			&& metaMenuActiveRoute === META_MENU_ROUTES.EVENT_SETTINGS
		) {
			return true;
		}

		return false;
	}, [metaMenuState, metaMenuActiveRoute]);

	const userIsInCanvas = useMemo(
		() => shotFeeds.some((feed: ShotFeed) => feed.presenter === user!.gr_user),
		[shotFeeds, user],
	);

	const userIsInNextShot = useMemo(() => {
		// eslint-disable-next-line camelcase
		const currentShotIndex = shots.findIndex((shot: Shot) => shot.shot_id === shotId);
		const nextShot = shots[currentShotIndex + 1];
		if (
			nextShot
			&& nextShot.shot_data.shotFeeds.some(
				(feed: ShotFeed) => feed.presenter === user!.gr_user,
			)
		) {
			return true;
		}
		return false;
	// eslint-disable-next-line camelcase
	}, [shots, shotId, user]);

	useEffect(() => {
		if (userIsInNextShot && shotStartTimestampEntity) {
			dispatch(NotificationActions.newNotifcation({
				type: NOTIFICATION_STYLES.TIMED,
				title: 'Get ready to go live',
				subtitle: 'Don\'t forget to look at the camera',
				icon: ICONS.ARROW_UP_FULL,
			}));
		}
		if (!userIsInNextShot || userIsInCanvas || roomState === ROOM_STATES.EDITING) {
			dispatch(NotificationActions.removeNotification());
		}
	}, [userIsInNextShot, userIsInCanvas, shotStartTimestampEntity, roomState]);

	useEffect(() => {
		const onStates = [
			BROADCAST_STATES.BROADCAST_STARTED,
			BROADCAST_STATES.BROADCAST_STARTING,
		];
		const offStates = [
			BROADCAST_STATES.OFF,
			BROADCAST_STATES.STARTING_UP,
			BROADCAST_STATES.READY,
			BROADCAST_STATES.BROADCAST_STOPPED,
			BROADCAST_STATES.DRAINING,
			BROADCAST_STATES.KILLED,
			BROADCAST_STATES.BROADCAST_FAILED,
			BROADCAST_STATES.BROADCAST_PREWARMING,
			BROADCAST_STATES.BROADCAST_PREWARM_FAILED,
		];
		// If you have refreshed and broadcasting has started or is
		// starting, goLiveButtonClicked will be false. Change to true so the user
		// cannot modify the destination.
		if (onStates.includes(serverState) && !goLiveButtonClicked) {
			dispatch(BroadcastActions.setGoLiveButtonClicked(true));
		}

		// If the user has ended the broadcast or it has failed, change
		// goLiveButtonClicked to false so the user
		// can modify the destination.
		if (offStates.includes(serverState) && goLiveButtonClicked) {
			dispatch(BroadcastActions.setGoLiveButtonClicked(false));
		}
	}, [serverState]);

	useEffect(() => {
		const checkChatNotifications = async () => {
			if (meeting && token) {
				const notifications = await LoadNotifications(token);

				// check if user has any unread message notifications and send toaster
				// eslint-disable-next-line @typescript-eslint/no-shadow
				if (notifications.some((notification: HomeNotification) => notification.notification
					&& notification.meeting === meeting.meeting
					&& !notification.has_been_seen
					// eslint-disable-next- line eqeqeq
					&& (notification.type == NOTIFICATION_TYPES.COMMENT_BASIC
						// eslint-disable-next-line eqeqeq
						|| notification.type == NOTIFICATION_TYPES.COMMENT_MENTION))) {
					Alerts.showAlert({
						message: 'Unread Chat',
						description: 'You have unread messages from one or more chat rooms or direct messages from when you last joined Greenroom.',
						onClick: () => {
							dispatch(SessionActions.setActiveTab(TABS.CHAT));
							dispatch(SessionActions.setRightDrawerOpen(true));
						},
						duration: 10000,
					});
				}
			}
		};

		checkChatNotifications();
	}, []);

	const channelLogo: string = useMemo(() => (
		getChannelLogoFromMeeting(meeting) || TellyChannelLogo
	), [meeting]);

	const showNotificationsView = useMemo(
		() => {
			const isCountingDown = shotDuration || autoDuration || (userIsInNextShot && !userIsInCanvas);
			const hasNotification = shotStartTimestampEntity && (userIsInNextShot || userIsInCanvas);
			return isCountingDown && hasNotification;
		},
		[
			shotDuration,
			autoDuration,
			userIsInNextShot,
			userIsInCanvas,
			shotStartTimestampEntity,
		],
	);

	const toggleHelpMenu = useCallback(() => {
		dispatch(SessionActions.setHelpMenuOpen(!helpMenuOpen));
		dispatch(SessionActions.setActiveHelpSubmenu(HELP_SUBMENU.NONE));
	}, [helpMenuOpen]);

	return (
		<header className="event-header">
			<div id="mode-line-portal" />
			<div className={classNames('header-left', showNotificationsView && 'show-notifications')}>
				<HeaderLeftControls
					channelLogo={channelLogo}
					title={meeting?.title || ''}
					backButtonHandler={backToBroadcastsListClicked}
				/>
				{scheduledStream && (
					<StreamBadge startTime={scheduledStream.startTime} socket={socket} host={host} />
				)}
			</div>

			<div className="header-center">
				{
					showNotificationsView
						? <NotificationsView
								key={shotId}
								socket={socket}
								userIsInCanvas={userIsInCanvas}
								userIsInNextShot={userIsInNextShot}
							/>
						: null
				}
			</div>

			<div className='header-right'>
				{(meeting && (currentTime > meeting.start_time)) && (
					<div style={{ marginRight: 16 }}></div>
				)}

				<div className="margin-right-small">
					<Clock />
				</div>

				<BroadcastControls socket={socket} />

				<Dialog ref={dialog} right={dialogRight} />

				{host && <div
					className="header-dividing-line margin-left-small"
					style={{ marginLeft: 16, marginRight: 8 }}
				/>}

				<div className="help-center-container">
					<div
						className={classNames(
							'header-small-button',
							helpMenuOpen && 'drawer-open',
						)}
						onMouseDown={toggleHelpMenu}
					>
						<Icon name={ICONS.QUESTION} size={20} color={COLORS.WHITE} />
					</div>
					<Help isOpen={helpMenuOpen} onClose={toggleHelpMenu} />
				</div>

				{host
					&& <div
						className={classNames(
							'header-small-button',
							settingsDrawerOpen && 'drawer-open',
						)}
						onClick={() => toggleDrawer(META_MENU_ROUTES.EVENT_SETTINGS)}
					>
						<Icon name={ICONS.SETTINGS} size={20} color={COLORS.WHITE} />
					</div>
				}

				{host
					&& <div
						className={classNames(
							'header-small-button',
							sharingDrawerOpen && 'drawer-open',
						)}
						onClick={() => toggleDrawer(META_MENU_ROUTES.SHARING)}
					>
						<Icon name={ICONS.SHARE} size={20} color={COLORS.WHITE} />
					</div>
				}

				<div
					className="header-dividing-line margin-left-small"
					style={{ marginLeft: 8, marginRight: 4 }}
				/>

				<div className="avatar-button" onClick={() => dispatch(SessionActions.setAvatarPopoverOpen(!avatarPopoverOpen))}>
					<Avatar
						user={user!}
						size="medium"
						noBorder={true}
						host={host}
						inMeeting={true}
						selected={activeTab === TABS.USER || avatarPopoverOpen || notesDrawerOpen}
						setAvatarPopoverOpen={(isOpen: boolean) => (
							dispatch(SessionActions.setAvatarPopoverOpen(isOpen))
						)}
						avatarPopoverOpen={avatarPopoverOpen}
					>
						<div className="avatar-options">
							<button className="avatar-option" onClick={() => {
								toggleDrawer(META_MENU_ROUTES.PROFILE);
								dispatch(SessionActions.setAvatarPopoverOpen(false));
							}}>
									<Icon name={ICONS.PRESENTERS} color={COLORS.GRAY} size={13} />Edit Profile
							</button>
							{
								// eslint-disable-next-line camelcase
								!watcherOnly && (
									<button className="avatar-option" onClick={() => {
										toggleDrawer(META_MENU_ROUTES.PRESENTER_SETTINGS);
										dispatch(SessionActions.setAvatarPopoverOpen(false));
									}}>
										<Icon name={ICONS.MICROPHONE_ON} color={COLORS.GRAY} size={13} />Devices
									</button>
								)
							}
							<button className="avatar-option" onClick={() => {
								dispatch(NotesActions.setShowWorkingAllNote(false));
								toggleDrawer(META_MENU_ROUTES.ALL_NOTES);
							}}>
									<Icon name={ICONS.NOTE} color={COLORS.GRAY} size={13} />All Notes
								</button>
							<button className="avatar-option" onClick={() => {
								backToBroadcastsListClicked(true);
								dispatch(SessionActions.setAvatarPopoverOpen(false));
							}}>
								<Icon name={ICONS.ARROW_LEFT} color={COLORS.GRAY} size={13} />Switch Room
							</button>
							<button className="avatar-option" onClick={() => {
								confirmSignout();
								dispatch(SessionActions.setAvatarPopoverOpen(false));
							}}>
								<Icon name={ICONS.LOG_OUT} color={COLORS.GRAY} size={13} />Sign Out
							</button>
						</div>
					</Avatar>
				</div>

				{showDebugUI && <span className="debug-opentok-session">{meeting?.opentok_session}</span>}
				{scheduledStreamCommsModalOpen && (
					<ScheduledStreamCommsModal
						isLive={roomState === ROOM_STATES.SHOWTIME && serverState === BROADCAST_STATES.BROADCAST_STARTED}
						goToSettings={() => {
							setIgnoredCommsModal(true);
							setScheduledStreamCommsModalOpen(false);
							dispatch(SessionActions.setMetaMenuState(META_MENU_STATES.OPENING));
							dispatch(SessionActions.setMetaMenuRoute(META_MENU_ROUTES.EVENT_SETTINGS));
							dispatch(SessionActions.setMetaMenuOption(MetaMenuOptions.Settings));
						}}
						handleClick={() => {
							setIgnoredCommsModal(true);
							setScheduledStreamCommsModalOpen(false);
						}}
					/>
				)}
			</div>
		</header>
	);
}
